(function () {
  "use strict";

  const states = ["idle", "idle-breathe", "hover-a", "hover-b", "bounce", "celebrate", "link-go"];
  const celebrateDuration = 1400;
  const hoverUpgradeDelay = 1500;
  const linkGoDelay = 520;
  const mascotInstances = new WeakMap();

  const getSafeNavigationUrl = (href) => {
    if (!href) {
      return null;
    }

    let url;
    try {
      url = new URL(href, window.location.href);
    } catch (_) {
      return null;
    }

    if (!["http:", "https:"].includes(url.protocol) || url.origin !== window.location.origin) {
      return null;
    }

    return url.href;
  };

  class SchoolMascot {
    constructor(element) {
      this.element = element;
      this.frames = {};
      this.state = "idle";
      this.hoverTimer = null;
      this.celebrateTimer = null;

      states.forEach((state) => {
        const frame = element.querySelector(`.mascot-school-frame--${state}`);
        if (frame) {
          this.frames[state] = frame;
          frame.addEventListener("error", () => {
            this.element.hidden = true;
          }, { once: true });
          if (frame.complete && frame.naturalWidth === 0) {
            this.element.hidden = true;
          }
        }
      });

      this.bindEvents();
      this.observeVisibility();
    }

    setState(nextState, frameState = nextState) {
      if (this.state === nextState || !this.frames[frameState]) {
        return;
      }

      this.frames[this.state]?.classList.remove("is-active");
      Object.values(this.frames).forEach((frame) => frame.classList.remove("is-active"));
      this.state = nextState;
      this.element.dataset.mascotSchoolState = nextState;
      this.frames[frameState].classList.add("is-active");
    }

    observeVisibility() {
      if (!("IntersectionObserver" in window)) {
        this.setState("idle-breathe");
        return;
      }

      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && this.state === "idle") {
            window.setTimeout(() => {
              if (this.state === "idle") {
                this.setState("idle-breathe");
              }
            }, 400);
          }
        });
      }, { threshold: .4 });

      observer.observe(this.element);
    }

    bindEvents() {
      this.element.addEventListener("mouseenter", () => this.startHover());
      this.element.addEventListener("touchstart", () => this.startHover(), { passive: true });
      this.element.addEventListener("mouseleave", () => this.endHover());
      this.element.addEventListener("touchend", () => this.endHover(), { passive: true });
      this.element.addEventListener("click", (event) => {
        if (this.element instanceof HTMLAnchorElement && this.element.href) {
          event.preventDefault();
          this.linkGo();
          return;
        }

        this.startHover();
        window.setTimeout(() => this.endHover(), 900);
      });
    }

    startHover() {
      if (["link-go", "celebrate", "bounce", "checkout-bounce"].includes(this.state)) {
        return;
      }

      this.clearHoverTimer();
      this.setState("hover-a");
      this.hoverTimer = window.setTimeout(() => {
        if (this.state === "hover-a") {
          this.setState("hover-b");
        }
      }, hoverUpgradeDelay);
    }

    endHover() {
      this.clearHoverTimer();
      if (this.state === "hover-a" || this.state === "hover-b") {
        this.setState("idle-breathe");
      }
    }

    clearHoverTimer() {
      if (this.hoverTimer) {
        window.clearTimeout(this.hoverTimer);
        this.hoverTimer = null;
      }
    }

    celebrate() {
      if (this.state === "link-go") {
        return;
      }

      this.clearHoverTimer();
      window.clearTimeout(this.celebrateTimer);
      this.setState("bounce");
      this.celebrateTimer = window.setTimeout(() => {
        this.setState("celebrate");
        this.celebrateTimer = window.setTimeout(() => {
          this.setState("idle-breathe");
        }, celebrateDuration);
      }, 550);
    }

    bounceOnly() {
      if (this.state === "link-go") {
        return;
      }

      this.clearHoverTimer();
      window.clearTimeout(this.celebrateTimer);
      this.setState("bounce");
      this.celebrateTimer = window.setTimeout(() => {
        this.setState("idle-breathe");
      }, 650);
    }

    checkoutCelebrate() {
      if (this.state === "link-go") {
        return;
      }

      this.clearHoverTimer();
      window.clearTimeout(this.celebrateTimer);
      this.setState("checkout-bounce", "bounce");
      this.celebrateTimer = window.setTimeout(() => {
        this.setState("celebrate");
        this.celebrateTimer = window.setTimeout(() => {
          this.setState("idle-breathe");
        }, celebrateDuration);
      }, 620);
    }

    shock() {
      if (this.state === "link-go") {
        return;
      }

      this.clearHoverTimer();
      window.clearTimeout(this.celebrateTimer);
      this.setState("shock", "hover-a");
      this.celebrateTimer = window.setTimeout(() => {
        this.setState("idle-breathe");
      }, 850);
    }

    linkGo() {
      const safeUrl = getSafeNavigationUrl(this.element.href);
      if (!safeUrl) {
        return;
      }

      this.clearHoverTimer();
      this.setState("link-go");
      window.setTimeout(() => {
        window.location.href = safeUrl;
      }, linkGoDelay);
    }
  }

  const initMascotSchool = (scope = document) => {
    scope.querySelectorAll?.("[data-mascot-school-character]").forEach((element) => {
      if (mascotInstances.has(element)) {
        return;
      }

      mascotInstances.set(element, new SchoolMascot(element));
    });

    scope.querySelectorAll?.("[data-mascot-school]").forEach((root) => {
      if (root.dataset.mascotSchoolReady === "true") {
        return;
      }

      root.dataset.mascotSchoolReady = "true";
      const sectionRoot = root.closest(".travel-school-carousel-section");
      const mascots = Array.from(root.querySelectorAll("[data-mascot-school-character]")).map((element) => mascotInstances.get(element)).filter(Boolean);
      const celebrateAll = () => mascots.forEach((mascot) => mascot.celebrate());

      sectionRoot?.querySelectorAll(".travel-school-carousel__content .button, .travel-school-carousel__content .link, .travel-school-carousel__products .ta-card-media-link, .travel-school-carousel__products .ta-product-meta h2 a, .travel-school-carousel__products .ta-add-to-cart").forEach((trigger) => {
        trigger.addEventListener("click", (event) => {
          const isContentButton = trigger.closest(".travel-school-carousel__content");
          const safeUrl = getSafeNavigationUrl(trigger.href);
          if (isContentButton && safeUrl) {
            event.preventDefault();
            celebrateAll();
            window.setTimeout(() => {
              window.location.href = safeUrl;
            }, linkGoDelay);
            return;
          }
          celebrateAll();
        });
      });

      document.addEventListener("cart:add", (event) => {
        if (!sectionRoot?.contains(event.target)) {
          return;
        }

        celebrateAll();
      });
    });
  };

  const getMascotsInScope = (scope) => Array.from(scope?.querySelectorAll?.("[data-mascot-school-character]") || []).map((element) => mascotInstances.get(element)).filter(Boolean);

  const runCartMascotHello = (scope = document) => {
    const cartDrawer = scope.closest?.("cart-drawer") || scope.querySelector?.("cart-drawer") || scope;

    cartDrawer.querySelectorAll?.(".mascot-school-wrap--compact[data-mascot-school-character]").forEach((element, index) => {
      element.classList.remove("is-cart-mascot-hello");

      window.setTimeout(() => {
        element.classList.add("is-cart-mascot-hello");

        window.setTimeout(() => {
          element.classList.remove("is-cart-mascot-hello");
        }, 800);
      }, index * 90);
    });
  };

  document.addEventListener("ta:school-mascot:init", (event) => initMascotSchool(event.detail?.scope || document));
  document.addEventListener("ta:school-mascot:shock", (event) => getMascotsInScope(event.detail?.scope || document).forEach((mascot) => mascot.shock()));
  document.addEventListener("ta:school-mascot:bounce", (event) => getMascotsInScope(event.detail?.scope || document).forEach((mascot) => mascot.bounceOnly()));
  document.addEventListener("ta:school-mascot:checkout", (event) => getMascotsInScope(event.detail?.scope || document).forEach((mascot) => mascot.checkoutCelebrate()));
  document.addEventListener("cart-drawer:refreshed", (event) => {
    initMascotSchool(event.target);
    runCartMascotHello(event.target);
  });

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => initMascotSchool(document));
  } else {
    initMascotSchool(document);
  }

  document.addEventListener("shopify:section:load", (event) => initMascotSchool(event.target));
})();
